import { isPartnerGI } from "@tm/insurance/utils";
import { getInternalSource } from "..";
import {
  AnalyticsEventObject,
  ComponentItem,
  ComponentViewData,
  ComponentViewInput,
  ComponentViewResult,
  ManualEventData,
  PageInitializationData,
  SearchContextParams,
} from "./types";
import { pushToSdkEvent } from "./xdmEvents";

// Hardcoded constants as per requirements
const HARDCODED_DATA = {
  event_tracking_context: {
    template_id: 52319,
    funnel_source: isPartnerGI() ? "gi" : "mmt",
    bu: "tripmoney",
    org: isPartnerGI() ? "gi" : "mmt",
    env: "qa",
    topic_name: "mmt_insurance_b2c_pdt_client_logging_v2",
    organisation: isPartnerGI() ? "GI" : "MMT",
    funnel_entry: "direct",
  },
  page_context: {
    lob: "travelinsurance",
    lob_category: "travelinsurance",
    page_url: typeof window !== "undefined" ? window.location.href : "",
    page_title: typeof document !== "undefined" ? document.title : "",
  },
};

// Memory storage for cached page data
let cachedPageData: PageInitializationData | null = null;
let pageExitListenersAdded = false;
let exitEventTriggered: Record<string, boolean> = {
  default: false,
};

// Memory storage for cached search context data
let cachedSearchContext: SearchContextParams | null = null;

// Memory storage for component view tracking
let componentViews: ComponentViewData[] = [];

/**
 * Remove empty keys from an object
 */
const removeEmptyKeys = (
  obj: Record<string, unknown>
): Record<string, unknown> => {
  const result: Record<string, unknown> = {};

  for (const key in obj) {
    const value = obj[key];

    // Check if value is not empty (not null, undefined, empty string, empty array, or empty object)
    if (
      value !== null &&
      value !== undefined &&
      value !== "" &&
      !(Array.isArray(value) && value.length === 0) &&
      !(typeof value === "object" && Object.keys(value).length === 0)
    ) {
      // If it's an object (but not array), recursively clean it
      if (typeof value === "object" && !Array.isArray(value)) {
        const cleanedValue = removeEmptyKeys(value as Record<string, unknown>);
        // Only add if the cleaned object is not empty
        if (Object.keys(cleanedValue).length > 0) {
          result[key] = cleanedValue;
        }
      } else {
        result[key] = value;
      }
    }
  }

  return result;
};

/**
 * Deep merge objects with priority to the second object
 */
const deepMerge = (
  target: Record<string, unknown>,
  source: Record<string, unknown>
): Record<string, unknown> => {
  const result = { ...target };

  for (const key in source) {
    if (source[key] !== null && source[key] !== undefined) {
      if (
        typeof source[key] === "object" &&
        !Array.isArray(source[key]) &&
        typeof target[key] === "object" &&
        !Array.isArray(target[key])
      ) {
        result[key] = deepMerge(
          (target[key] as Record<string, unknown>) || {},
          source[key] as Record<string, unknown>
        );
      } else {
        result[key] = source[key];
      }
    }
  }

  return result;
};

/**
 * Merge search context parameters with cached data
 * Only updates the provided keys, keeps existing values for others
 */
const mergeSearchContext = (
  newSearchContext: SearchContextParams
): SearchContextParams => {
  if (!cachedSearchContext) {
    // If no cached data, use the new data as is
    cachedSearchContext = { ...newSearchContext };
    return cachedSearchContext;
  }

  // Merge new data with cached data, new data takes priority
  cachedSearchContext = {
    ...cachedSearchContext,
    ...newSearchContext,
  };

  return cachedSearchContext;
};

export const getSearchContext = (searchContext?: SearchContextParams) => {
  if (!searchContext) {
    return {};
  }
  const routeDetails =
    searchContext?.countries &&
    Array.isArray(searchContext.countries) &&
    searchContext.countries.length > 0
      ? searchContext.countries.map((country) => ({
          to: {
            location: {
              country_code: country.code,
              name: country.name,
            },
            ...(country?.locusId &&
              country?.locusType && {
                locus: {
                  locus_id: country?.locusId,
                  locus_type: country?.locusType,
                },
              }),
          },
        }))
      : [];

  let toDetails = null;

  searchContext?.countries &&
  Array.isArray(searchContext.countries) &&
  searchContext.countries.length === 1
    ? searchContext.countries.forEach((country) => {
        toDetails = {
          to: {
            location: {
              country_code: country?.code,
              name: country?.name,
            },
            ...(country?.locusId &&
              country?.locusType && {
                locus: {
                  country: country?.name,
                  locus_id: country?.locusId,
                  locus_type: country?.locusType,
                },
              }),
          },
        };
      })
    : null;
  const searchContextData = {
    ...(toDetails || {}),
    from_date_time: searchContext?.fromDateTime
      ? Number(searchContext.fromDateTime) * 1000
      : 0,
    to_date_time: searchContext?.toDateTime
      ? Number(searchContext.toDateTime) * 1000
      : 0,
    pax: {
      count: searchContext?.travellers ?? 0,
    },
    ...(routeDetails.length > 0 && { route_details: routeDetails ?? [] }),
    ...(searchContext?.journeyType && {
      journey_type: searchContext?.journeyType,
    }),
  };

  return removeEmptyKeys(searchContextData);
};

/**
 * Utility function to handle search context merging logic
 * Returns the merged search context or null if no context is available
 */
export const getMergedSearchContext = (searchContext?: SearchContextParams) => {
  let searchContextData;
  if (searchContext) {
    searchContextData = mergeSearchContext(searchContext);
  } else if (cachedSearchContext) {
    // Use cached search context if no new one provided
    searchContextData = cachedSearchContext;
  }
  searchContextData = getSearchContext(searchContextData);
  return searchContextData;
};

export const getSelectedCountriesFromLocalStorage = (countryCods: string[]) => {
  const countries = localStorage.getItem("TRAVEL_INSURANCE_COUNTRIES");
  if (countries) {
    const getCountries = JSON.parse(countries);
    return getCountries.filter((country: any) =>
      countryCods.includes(country.code)
    );
  }
  return [];
};

/**
 * Extracts content_details array for L1 generation.
 * Accepts Java-style components list (ComponentItem[]) or plain content_details (ComponentViewData[]).
 */
const getContentDetailsForL1 = (
  components?: ComponentItem[] | ComponentViewData[]
): ComponentViewData[] => {
  if (!components || !Array.isArray(components) || components.length === 0) {
    return [];
  }
  const first = components[0] as ComponentItem | ComponentViewData;
  if (
    first &&
    "content_details" in first &&
    Array.isArray(first.content_details)
  ) {
    return (components as ComponentItem[]).flatMap(
      (c) => c.content_details ?? []
    );
  }
  return components as ComponentViewData[];
};

/**
 * Generates components_l1 string from components array
 * @param components Array of component objects with id, position, name, and type
 * @returns Formatted components_l1 string or empty string if no components
 */
const generateComponentsL1 = (components?: any[]): string => {
  if (!components || !Array.isArray(components) || components.length === 0) {
    return "";
  }

  return components
    .filter((component) => component.id && component.position && component.type)
    .map((component) => {
      const { id, position, type } = component;
      const v = position.v || 0;
      const h = position.h || 0; // Default to 0 if h is not provided
      return `${id}:${type}:V_${v}:H_${h}`;
    })
    .join("|");
};

/**
 * Generates multicity destination location name from route details
 * @param routeDetails Array of route detail objects with to.location.name
 * @returns Formatted destination location names separated by colons or empty string if no routes
 */
const generateMulticityDestinationLocationName = (
  routeDetails?: any[]
): string => {
  if (
    !routeDetails ||
    !Array.isArray(routeDetails) ||
    routeDetails.length === 0
  ) {
    return "";
  }

  return routeDetails
    .filter((route) => route?.to?.location?.name)
    .map((route) => route.to.location.name)
    .join(":");
};

/**
 * Creates the final analytics event object by merging all data layers
 */
export const createAnalyticsEvent = (
  pageData: PageInitializationData,
  eventData?: Partial<ManualEventData>
): AnalyticsEventObject => {
  // Start with hardcoded data as base
  let mergedData: Record<string, unknown> = deepMerge(
    {},
    HARDCODED_DATA as Record<string, unknown>
  );

  // Merge with cached page data
  mergedData = deepMerge(mergedData, pageData as Record<string, unknown>);

  // Merge with event-specific data (highest priority)
  if (eventData) {
    mergedData = deepMerge(mergedData, eventData as Record<string, unknown>);
  }

  const internalSource = getInternalSource();
  if (internalSource) {
    mergedData = deepMerge(mergedData, {
      event_tracking_context: { traffic_source: internalSource },
    });
  }

  // Remove empty keys from the final object
  const cleanedData = removeEmptyKeys(mergedData);

  return cleanedData as unknown as AnalyticsEventObject;
};

/**
 * Handles page exit events
 */
export const handlePageExit = () => {
  // Prevent duplicate exit events
  if (
    exitEventTriggered[cachedPageData?.page_context?.page_name || "default"]
  ) {
    return;
  }

  const mergedSearchContext = getMergedSearchContext();
  const componentViews = getComponentViewsAndClear();

  const adobeAnalytics = getAdobeAnalytics(
    mergedSearchContext,
    componentViews?.components
  );

  if (cachedPageData) {
    const exitEvent = createAnalyticsEvent(cachedPageData, {
      event_detail: {
        ...(componentViews?.components &&
          componentViews.components.length > 0 && {
            components: [
              { content_details: componentViews.components },
            ] as ComponentItem[],
          }),
        event_type: "action",
        event_name: "page-exit",
      },
      ...(mergedSearchContext && { search_context: mergedSearchContext }),
      adobe_analytics: adobeAnalytics,
    });
    pushToSdkEvent(exitEvent);

    // Mark exit event as triggered to prevent duplicates
    exitEventTriggered[cachedPageData?.page_context?.page_name || "default"] =
      true;
  } else {
    console.warn("Page exit event triggered but no cached page data available");
  }
};

export const generateErrorDetailsListL1 = (errorDetailsList?: any[]) => {
  if (
    !errorDetailsList ||
    !Array.isArray(errorDetailsList) ||
    errorDetailsList.length === 0
  ) {
    return "";
  }
  return errorDetailsList
    .map(
      (error) =>
        `${error?.code ? `${error?.code}:` : ""}${
          error?.message ? `${error?.message}` : ""
        }`
    )
    .join("|");
};

export const getAdobeAnalytics = (
  searchContext?: any,
  components?: ComponentItem[] | ComponentViewData[],
  errorDetailsList?: any[]
) => {
  const contentDetails = getContentDetailsForL1(components);
  const componentsL1 = generateComponentsL1(contentDetails);
  const multicityDestinationLocationName =
    generateMulticityDestinationLocationName(searchContext?.route_details);
  const errorDetailsListL1 = generateErrorDetailsListL1(errorDetailsList);

  return {
    ...(multicityDestinationLocationName && {
      multicity_destination_location_name_c51: multicityDestinationLocationName,
    }),
    ...(componentsL1 && { components_l1: componentsL1 }),
    ...(errorDetailsListL1 && { error_details_v11: errorDetailsListL1 }),
  };
};

/**
 * Sets up page exit event listeners automatically
 */
export const setupPageExitListeners = () => {
  if (!pageExitListenersAdded && typeof window !== "undefined") {
    // beforeunload - fires when the page is about to be unloaded
    window.addEventListener("beforeunload", handlePageExit);

    // pagehide - fires when the page is hidden (better for mobile)
    window.addEventListener("pagehide", handlePageExit);

    // visibilitychange - fires when the page becomes hidden (good for tab switching)
    document.addEventListener("visibilitychange", () => {
      if (document.visibilityState === "hidden") {
        handlePageExit();
      }
    });

    pageExitListenersAdded = true;
  }
};

// Note: Exit listeners will be set up when initializePageTracking is called

/**
 * Removes page exit event listeners (for cleanup)
 */
const removePageExitListeners = () => {
  if (pageExitListenersAdded && typeof window !== "undefined") {
    window.removeEventListener("beforeunload", handlePageExit);
    window.removeEventListener("pagehide", handlePageExit);
    document.removeEventListener("visibilitychange", () => {
      if (document.visibilityState === "hidden") {
        handlePageExit();
      }
    });
    pageExitListenersAdded = false;
  }
};

/**
 * Clear cached page data and remove event listeners (for cleanup)
 */
export const clearPageTracking = (): void => {
  cachedPageData = null;
  exitEventTriggered = {
    default: false,
  };
  cachedSearchContext = null;

  removePageExitListeners();
};

/**
 * Get current cached page data (for debugging/testing)
 */
export const getCachedPageData = (): PageInitializationData | null => {
  return cachedPageData;
};

/**
 * Cache page data for current session
 */
export const setCachedPageData = (pageData: PageInitializationData): void => {
  cachedPageData = pageData;
};

/**
 * Get current cached search context data (for debugging/testing)
 */
export const getCachedSearchContext = (): SearchContextParams | null => {
  return cachedSearchContext;
};

/**
 * Track a component view
 */
export const trackComponentView = (input: ComponentViewInput): void => {
  const existingIndex = componentViews.findIndex(
    (component) =>
      component.name === input.name && component.type === input.type
  );

  if (existingIndex !== -1) {
    // Update existing component view frequency
    componentViews[existingIndex].view_frequency += 1;
  } else {
    // Create new component view entry
    const componentData = {
      id: generateComponentId(),
      name: input.name,
      type: input.type,
      tracking_key: input.tracking_key,
      card_component: input.card_component,
      view_frequency: 1,
      position: {
        v: componentViews.length + 1,
      },
    };

    const cleanedData = removeEmptyKeys(componentData);
    const newComponent: ComponentViewData =
      cleanedData as unknown as ComponentViewData;
    componentViews.push(newComponent);
  }
};

/**
 * Get all tracked component views and clear cache
 */
export const getComponentViewsAndClear = (): ComponentViewResult => {
  const result: ComponentViewResult = {
    components: [...componentViews],
    totalComponents: componentViews.length,
    totalViews: componentViews.reduce(
      (sum, component) => sum + component.view_frequency,
      0
    ),
    retrievedAt: Date.now(),
  };

  // Clear the cached data
  componentViews = [];

  return result;
};

/**
 * Generate unique component ID
 */
const generateComponentId = (): string => {
  return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, function (c) {
    const r = (Math.random() * 16) | 0;
    const v = c === "x" ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
};

export type PageName =
  | "LANDING"
  | "LANDING_RETAIL"
  | "LANDING_MYPARTNER"
  | "LISTING"
  | "REVIEW"
  | "PAYMENT"
  | "THANK_YOU_POLICY"
  | "THANK_YOU_PASSPORT"
  | "ADD_PASSPORT"
  | "ORDER_SUMMARY"
  | "ORDER_DETAILS"
  | "COMMON_LANDING"
  | "COMMON_LANDING_RETAIL"
  | "LISTING_MYPARTNER"
  | "REVIEW_MYPARTNER"
  | "THANK_YOU_POLICY_MYPARTNER"
  | "PAYMENT_MYPARTNER";

//Expected 'landing' | 'listing' | 'detail' | 'review' | 'payment' | 'thankyou',
export const getPageNameAnalytics = (pageName: PageName) => {
  switch (pageName) {
    case "LANDING":
      return "landing";
    case "LANDING_RETAIL":
      return "landing_retail";
    case "LANDING_MYPARTNER":
      return "landing_mypartner";
    case "LISTING":
      return "listing";
    case "REVIEW":
      return "review";
    case "PAYMENT":
      return "payment";
    case "THANK_YOU_POLICY":
      return "thankyou";
    case "THANK_YOU_PASSPORT":
      return "thankyou";
    case "ADD_PASSPORT":
      return "thankyou";
    case "ORDER_SUMMARY":
      return "detail";
    case "ORDER_DETAILS":
      return "detail";
    case "COMMON_LANDING":
      return "landing";
    case "COMMON_LANDING_RETAIL":
      return "landing";
    case "LISTING_MYPARTNER":
      return "listing";
    case "REVIEW_MYPARTNER":
      return "review";
    case "THANK_YOU_POLICY_MYPARTNER":
      return "thankyou";
    case "PAYMENT_MYPARTNER":
      return "payment";
    default:
      return "landing";
  }
};
